import { KeyboardInput } from './KeyboardInput.js';
import { TouchControls } from './TouchControls.js';

// InputManager is the single thing the game code reads from. It owns the
// keyboard and touch backends and merges their state, so neither GameLoop
// nor HUD needs to know which input method is active.
//
// Jump model:
//   - jumpDown()/jumpUp() are called by backends on press/release.
//   - GameLoop polls consumeJumpRelease() each frame; if a release happened,
//     it returns the hold duration in seconds. Tap = small hop; held longer
//     than HOLD_THRESHOLD = charged jump with a 360 spin.
//   - jumpHeldFor() lets the GameLoop see how long the button has been held
//     so far (for charge-up UI feedback).
export class InputManager {
  static HOLD_THRESHOLD = 0.18;

  constructor() {
    this.steer = 0;
    this.tucking = false;
    this.itemEdge = false;
    this.trickQueue = [];

    this._jumpDownAt = 0; // performance.now() ms when last press began; 0 if not held
    this._jumpReleasedHold = -1; // seconds the most recent release was held for; -1 if no release pending
    // Edge: Space was pressed this frame. Used by spectator mode to cycle
    // through racers; the jump system reads jumpDown/jumpUp instead so the
    // two don't collide.
    this._spaceEdge = false;

    // Camera yaw offset driven by a 2-finger touch pan. Positive = orbit
    // the camera CCW around the racer. Eases back to 0 each frame.
    this.viewYawOffset = 0;

    // View mode for the chase cam: 'chase' (default, behind+slightly-above)
    // or 'overhead' (high cam, downward angle so opponents are visible).
    // Toggled by V on the keyboard.
    this.viewMode = 'chase';

    this.keyboard = new KeyboardInput(this);
    this.touch = new TouchControls(this);
  }

  enable() {
    this.keyboard.enable();
    this._detectTouch();
  }

  disable() {
    this.keyboard.disable();
    this.touch.disable();
  }

  _detectTouch() {
    const hasTouch = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;
    if (hasTouch) {
      // Mark the body BEFORE enabling touch controls so the mobile-controls
      // container flips from display:none to display:block. nipplejs measures
      // the joystick zone's bounds at create-time — if we enable touch first,
      // the zone has zero dimensions and the joystick can't register any
      // touches (everything else, including 2-finger pan via window
      // listeners, still works, which is the symptom we hit).
      document.body.classList.add('mobile');
      this.touch.enable();
    }
  }

  // --- writers, called by backends ---
  setSteer(v) { this.steer = Math.max(-1, Math.min(1, v)); }
  setTucking(v) { this.tucking = !!v; }
  pressItem() { this.itemEdge = true; }
  pressTrick(letter) { this.trickQueue.push(letter); }

  jumpDown() {
    this._spaceEdge = true;
    if (this._jumpDownAt > 0) return; // already held; ignore key-repeat
    this._jumpDownAt = performance.now();
  }
  jumpUp() {
    if (this._jumpDownAt === 0) return;
    const heldSec = (performance.now() - this._jumpDownAt) / 1000;
    this._jumpDownAt = 0;
    this._jumpReleasedHold = heldSec;
  }

  // Camera offset writers / readers.
  setViewYawOffset(v) { this.viewYawOffset = v; }
  // Eases the camera offset toward 0 — called by GameLoop each frame so the
  // camera drifts back behind the racer after a 2-finger pan release.
  decayViewYawOffset(dt) {
    if (this._panActive) return; // user is actively panning; don't decay
    const k = 1 - Math.exp(-dt * 4);
    this.viewYawOffset *= (1 - k);
    if (Math.abs(this.viewYawOffset) < 0.001) this.viewYawOffset = 0;
  }
  setPanActive(active) { this._panActive = active; }

  // Toggle the chase camera between chase + overhead. Called by KeyboardInput
  // on V. Returns the new mode so debug code can log it.
  toggleViewMode() {
    this.viewMode = this.viewMode === 'chase' ? 'overhead' : 'chase';
    return this.viewMode;
  }
  getViewMode() { return this.viewMode; }

  // --- readers, called by GameLoop ---
  getSteer() { return this.steer; }
  isTucking() { return this.tucking; }
  getViewYawOffset() { return this.viewYawOffset; }
  jumpHeldFor() {
    return this._jumpDownAt === 0 ? 0 : (performance.now() - this._jumpDownAt) / 1000;
  }
  // Returns held duration in seconds if the jump was just released; null otherwise.
  consumeJumpRelease() {
    if (this._jumpReleasedHold < 0) return null;
    const v = this._jumpReleasedHold;
    this._jumpReleasedHold = -1;
    return v;
  }
  consumeItem() {
    const fired = this.itemEdge;
    this.itemEdge = false;
    return fired;
  }
  consumeTrickPresses() {
    const out = this.trickQueue;
    this.trickQueue = [];
    return out;
  }
  // Edge consumer: returns true if Space was pressed since the last call.
  consumeSpaceEdge() {
    const fired = this._spaceEdge;
    this._spaceEdge = false;
    return fired;
  }
}
